import { useState } from 'react';
import { ApiError, api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';
import { Banner, Loading } from './states';

interface Status {
  active: boolean;
  reason: string;
}

/**
 * Turns tenant maintenance mode on or off. While on, the control plane refuses changes
 * for this tenant (reads still work); the toggle itself always works, so a freeze can
 * always be lifted. Tenant admins only.
 */
export function MaintenancePanel(): JSX.Element {
  const t = useT();
  const { can } = useAuth();
  const status = useAsync(
    () =>
      can('tenant:admin') ? api.get<Status>('/operations/maintenance') : Promise.resolve(null),
    [],
  );
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!can('tenant:admin')) return <></>;

  const set = (active: boolean): void => {
    setError(null);
    void (async () => {
      try {
        await api.put('/operations/maintenance', { active, reason });
        status.reload();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : t('errors.unexpected'));
      }
    })();
  };

  if (status.loading) return <Loading />;
  const active = status.data?.active ?? false;

  return (
    <div className="card">
      <h2>{t('maintenance.title')}</h2>
      <p>{t('maintenance.intro')}</p>
      {error ? <Banner variant="error">{error}</Banner> : null}
      {active ? (
        <Banner variant="warning">
          {t('maintenance.active')}
          {status.data?.reason ? ` — ${status.data.reason}` : ''}
        </Banner>
      ) : null}

      {active ? (
        <button type="button" className="btn btn--primary" onClick={() => set(false)}>
          {t('maintenance.disable')}
        </button>
      ) : (
        <form
          className="form form--inline"
          onSubmit={(e) => {
            e.preventDefault();
            set(true);
          }}
        >
          <label className="field">
            <span>{t('maintenance.reason')}</span>
            <input value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
          <button type="submit" className="btn btn--danger">
            {t('maintenance.enable')}
          </button>
        </form>
      )}
    </div>
  );
}
